import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { Card } from '../../components/ui/Card'
import { Field } from '../../components/ui/Field'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import { Pagination } from '../../components/ui/Pagination'
import { useClientPagination } from '../../hooks/useClientPagination'

interface Product {
  id: number
  sku: string
  name: string
  brand: string
  active: boolean
}

interface LabAnalysis {
  id: number
  productId: number
  lotNo: string
  expiryDate: string
  lab: string
  analysisType: string
  cost: string | null
  conclusion: string
  product: { id: number; sku: string; name: string; brand: string }
  createdAt: string
}

export function AnalizeLaboratorPage() {
  const queryClient = useQueryClient()
  const permissions = useAuthStore((s) => s.permissions)
  const canWrite = permissions.includes('lab:write')

  const [createOpen, setCreateOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<LabAnalysis | null>(null)

  const query = useQuery<{ analyses: LabAnalysis[] }>({
    queryKey: ['lab-analyses'],
    queryFn: async () => (await api.get<{ analyses: LabAnalysis[] }>('/lab-analyses')).data,
  })

  const productsQuery = useQuery<{ products: Product[] }>({
    queryKey: ['products'],
    queryFn: async () => (await api.get<{ products: Product[] }>('/products')).data,
  })

  const createMutation = useMutation({
    mutationFn: async (data: Partial<LabAnalysis>) => (await api.post('/lab-analyses', data)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lab-analyses'] })
      toast.success('Analiză adăugată')
      setCreateOpen(false)
    },
    onError: showError,
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<LabAnalysis> }) =>
      (await api.patch(`/lab-analyses/${id}`, data)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lab-analyses'] })
      toast.success('Salvat')
      setEditTarget(null)
    },
    onError: showError,
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/lab-analyses/${id}`)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lab-analyses'] })
      toast.success('Șters')
    },
  })

  const analyses = useMemo(() => query.data?.analyses ?? [], [query.data])
  const pag = useClientPagination(analyses, 10)
  const products = useMemo(
    () => (productsQuery.data?.products ?? []).filter((p) => p.active),
    [productsQuery.data],
  )

  const today = useMemo(() => new Date(), [])
  const isExpiringSoon = (iso: string) => {
    const d = new Date(iso)
    const days = Math.floor((d.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
    return days >= 0 && days <= 60
  }
  const isExpired = (iso: string) => new Date(iso) < today

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Analize laborator</h1>
        {canWrite && <Button onClick={() => setCreateOpen(true)}>+ Analiză nouă</Button>}
      </div>

      <Card description="Buletine de analize la lot. Sortat după data expirării. Lot-urile expirate sau care expiră în 60 zile sunt evidențiate.">
        {query.isLoading ? (
          <p className="text-sm text-b4l-text-muted">Se încarcă...</p>
        ) : analyses.length === 0 ? (
          <div className="rounded border border-dashed border-b4l-border p-8 text-center text-sm text-b4l-text-muted">
            Nicio analiză. {canWrite && 'Apasă „+ Analiză nouă”.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-b4l-border text-left text-xs uppercase tracking-wide text-b4l-text-muted">
                  <th className="py-2 pr-3 font-medium">SKU</th>
                  <th className="py-2 pr-3 font-medium">Produs</th>
                  <th className="py-2 pr-3 font-medium">Lot</th>
                  <th className="py-2 pr-3 font-medium">Expirare</th>
                  <th className="py-2 pr-3 font-medium">Laborator</th>
                  <th className="py-2 pr-3 font-medium">Tip</th>
                  <th className="py-2 pr-3 font-medium text-right">Cost</th>
                  <th className="py-2 pr-3 font-medium">Concluzie</th>
                  {canWrite && <th className="py-2 pr-3 font-medium">Acțiuni</th>}
                </tr>
              </thead>
              <tbody>
                {pag.paginated.map((a) => (
                  <tr key={a.id} className="border-b border-b4l-border last:border-0">
                    <td className="py-2 pr-3 font-mono text-xs">{a.product.sku}</td>
                    <td className="py-2 pr-3 max-w-[220px] truncate" title={a.product.name}>
                      {a.product.name}
                    </td>
                    <td className="py-2 pr-3 font-mono text-[11px]">{a.lotNo}</td>
                    <td
                      className={`py-2 pr-3 text-xs whitespace-nowrap ${
                        isExpired(a.expiryDate)
                          ? 'text-b4l-red-dark font-medium'
                          : isExpiringSoon(a.expiryDate)
                            ? 'text-b4l-amber-dark font-medium'
                            : ''
                      }`}
                    >
                      {new Date(a.expiryDate).toLocaleDateString('en-GB')}
                      {isExpired(a.expiryDate)
                        ? ' (expirat)'
                        : isExpiringSoon(a.expiryDate)
                          ? ' ⚠'
                          : ''}
                    </td>
                    <td className="py-2 pr-3 text-xs">{a.lab}</td>
                    <td className="py-2 pr-3 text-xs">{a.analysisType}</td>
                    <td className="py-2 pr-3 font-mono text-xs text-right">
                      {a.cost ? Number(a.cost).toFixed(2) : '—'}
                    </td>
                    <td className="py-2 pr-3 max-w-[300px] truncate" title={a.conclusion}>
                      {a.conclusion}
                    </td>
                    {canWrite && (
                      <td className="py-2 pr-3">
                        <div className="flex gap-1">
                          <Button
                            variant="secondary"
                            onClick={() => setEditTarget(a)}
                            className="!px-2 !py-1 !text-xs"
                          >
                            Editează
                          </Button>
                          <Button
                            variant="danger"
                            onClick={() => {
                              if (confirm(`Ștergi analiza lot ${a.lotNo}?`))
                                deleteMutation.mutate(a.id)
                            }}
                            className="!px-2 !py-1 !text-xs"
                          >
                            Șterge
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="mt-3">
          <Pagination
            page={pag.page}
            pageSize={pag.pageSize}
            total={pag.total}
            onPageChange={pag.setPage}
            onPageSizeChange={pag.setPageSize}
            itemLabel="analize"
          />
        </div>
      </Card>

      {createOpen && (
        <LabFormModal
          title="Analiză nouă"
          submitLabel="Adaugă"
          products={products}
          onClose={() => setCreateOpen(false)}
          onSubmit={(d) => createMutation.mutate(d)}
          submitting={createMutation.isPending}
        />
      )}
      {editTarget && (
        <LabFormModal
          title={`Editează analiza lot ${editTarget.lotNo}`}
          submitLabel="Salvează"
          initial={editTarget}
          products={products}
          onClose={() => setEditTarget(null)}
          onSubmit={(d) => updateMutation.mutate({ id: editTarget.id, data: d })}
          submitting={updateMutation.isPending}
        />
      )}
    </div>
  )
}

function LabFormModal({
  title,
  submitLabel,
  initial,
  products,
  onClose,
  onSubmit,
  submitting,
}: {
  title: string
  submitLabel: string
  initial?: LabAnalysis
  products: Product[]
  onClose: () => void
  onSubmit: (d: Partial<LabAnalysis>) => void
  submitting: boolean
}) {
  const [productId, setProductId] = useState<string>(
    initial?.productId ? String(initial.productId) : '',
  )
  const [lotNo, setLotNo] = useState(initial?.lotNo ?? '')
  const [expiryDate, setExpiryDate] = useState(
    initial?.expiryDate ? initial.expiryDate.slice(0, 10) : '',
  )
  const [lab, setLab] = useState(initial?.lab ?? '')
  const [analysisType, setAnalysisType] = useState(initial?.analysisType ?? '')
  const [cost, setCost] = useState(initial?.cost ?? '')
  const [conclusion, setConclusion] = useState(initial?.conclusion ?? '')

  const submit = () => {
    if (
      !productId ||
      !lotNo.trim() ||
      !expiryDate ||
      !lab.trim() ||
      !analysisType.trim() ||
      !conclusion.trim()
    ) {
      toast.error('Toate câmpurile (mai puțin cost) sunt obligatorii')
      return
    }
    onSubmit({
      productId: Number(productId),
      lotNo: lotNo.trim(),
      expiryDate: new Date(expiryDate).toISOString(),
      lab: lab.trim(),
      analysisType: analysisType.trim(),
      cost: cost ? String(Number(cost)) : null,
      conclusion: conclusion.trim(),
    } as unknown as Partial<LabAnalysis>)
  }

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={title}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Anulează
          </Button>
          <Button onClick={submit} loading={submitting}>
            {submitLabel}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-b4l-text-muted">Produs</label>
          <select
            value={productId}
            onChange={(e) => setProductId(e.target.value)}
            disabled={!!initial}
            className="w-full rounded border border-b4l-border px-3 py-2 text-sm disabled:bg-b4l-bg"
          >
            <option value="">— alege produs —</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.sku} — {p.name}
              </option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Lot" value={lotNo} onChange={(e) => setLotNo(e.target.value)} />
          <Field
            label="Expirare"
            type="date"
            value={expiryDate}
            onChange={(e) => setExpiryDate(e.target.value)}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field
            label="Laborator"
            value={lab}
            onChange={(e) => setLab(e.target.value)}
            hint="ex: ICA, NeoLab, Synevo"
          />
          <Field
            label="Tip analiză"
            value={analysisType}
            onChange={(e) => setAnalysisType(e.target.value)}
            hint="microbiologie, chimie, metale grele, etc."
          />
        </div>
        <Field
          label="Cost RON"
          type="number"
          step="0.01"
          value={cost}
          onChange={(e) => setCost(e.target.value)}
        />
        <div>
          <label className="mb-1 block text-xs font-medium text-b4l-text-muted">Concluzie</label>
          <textarea
            value={conclusion}
            onChange={(e) => setConclusion(e.target.value)}
            rows={3}
            className="w-full rounded border border-b4l-border px-3 py-2 text-sm"
          />
        </div>
      </div>
    </Modal>
  )
}

function showError(err: unknown) {
  const message =
    (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
      ?.message ?? 'Eroare'
  toast.error(message)
}
